import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { LinearProgress, Radio } from "@material-ui/core";
import SkeletonTable from "components/SkeletonTable/SkeletonTable";
import ReactTableV2 from "components/ReactTable/ReactTableV2";
import { BASE_URL_API_ECOMMERCE, BASIC_AUTH } from "services/Http";
import useDealerId from "hooks/useDealerId";
import useProductImageUrl from "hooks/useProductImageUrl";
import useDealerCatalogs from "hooks/useDealerCatalogs";
import http from "services/Http";
import { isURL } from "helpers";
import CurrencyValueDisplay from "components/CurrencyValueDisplay/CurrencyValueDisplay";

let scriptLoaded = false;
const accumulatedRecords = [];
let initialRecordsRendered = false;

export default function PartSelectionDialog({
    orderDealerId = "",
    partNumber = "",
    onProductSelect = () => { },
}) {
    const xhrRef = useRef(null);

    const dealerIdFromHook = useDealerId();
    const effectiveDealerId = orderDealerId || dealerIdFromHook;

    const dealerCatalogs = useDealerCatalogs(effectiveDealerId);
    console.log("dealerCatalogs", dealerCatalogs);

    const [products, setProducts] = useState([]);
    const [noRecords, setNoRecords] = useState(false);

    const { getProductImageUrl } = useProductImageUrl();

    /* =========================
       INIT EFFECT
    ========================== */
    useEffect(() => {
        if (!effectiveDealerId || dealerCatalogs.length === 0 || !partNumber) return;

        const isMotor = dealerCatalogs.some((dc) => dc.catalogName === "motor");
        const script = document.createElement("script");

        if (isMotor) {
            script.src = "/jsonpipe.js";
            script.async = true;
            document.body.appendChild(script);

            script.onload = () => {
                if (!scriptLoaded) {
                    searchProductMotor(partNumber);
                    scriptLoaded = true;
                }
            };
        } else {
            searchProductAllCatalogs(partNumber, dealerCatalogs);
        }

        return () => {
            if (isMotor) {
                if (xhrRef.current) {
                    xhrRef.current.abort();
                    xhrRef.current = null;
                }
                scriptLoaded = false;
                accumulatedRecords.length = 0;
                initialRecordsRendered = false;
                if (document.body.contains(script)) {
                    document.body.removeChild(script);
                }
            }

            setProducts([]);
            setNoRecords(false);
        };
    }, [effectiveDealerId, dealerCatalogs, partNumber]);

    /* =========================
       MOTOR (CHUNKS)
    ========================== */
    const searchProductMotor = useCallback(
        (keyword) => {
            const cleanPartNumber = keyword.replace(/[^a-zA-Z0-9]/g, "");
            const url = `${BASE_URL_API_ECOMMERCE}/suggestions-search?dlid=${effectiveDealerId}`;

            xhrRef.current = window.jsonpipe.flow(url, {
                delimiter: "#####",
                success: processChunk,
                complete: () => {
                    xhrRef.current = null;
                    if (!initialRecordsRendered) {
                        setNoRecords(true);
                    }
                },
                timeout: 3000000,
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Basic ${BASIC_AUTH}`,
                },
                data: JSON.stringify({
                    keyword: cleanPartNumber,
                    catalog: "motor",
                    locale: "en",
                    allMakes: true,
                }),
            });
        },
        [effectiveDealerId],
    );

    function processChunk(chunk) {
        accumulatedRecords.push(...chunk);

        if (accumulatedRecords.length >= 10 && !initialRecordsRendered) {
            const initial = accumulatedRecords.splice(0, 10);
            setProducts(initial);
            initialRecordsRendered = true;
        }
    }

    /* =========================
       NON-MOTOR (ALL CATALOGS)
    ========================== */
    const searchProductAllCatalogs = useCallback(
        async (keyword, catalogs, offset = 0, limit = 10) => {
            try {
                const requests = catalogs.map((dc) => {
                    const url = `${BASE_URL_API_ECOMMERCE}/suggestions-search?dlid=${effectiveDealerId}&offset=${offset}&limit=${limit}`;

                    return http
                        .post(url, {
                            keyword,
                            catalog: dc.catalogName,
                            locale: "en",
                            allMakes: true,
                        })
                        .then((r) => {
                            const data = r.data;
                            const items = data?.products ?? data ?? [];
                            return items.map((p) => ({
                                ...p,
                                catalog: dc,
                            }));
                        })
                        .catch((err) => {
                            console.error(`Search error for catalog ${dc.catalogName}:`, err);
                            return [];
                        });
                });

                const results = (await Promise.all(requests)).flat();

                // Deduplicar por id + catalog
                const unique = Object.values(
                    results.reduce((acc, p) => {
                        const key = `${p.id}-${p.catalog?.catalogName}`;
                        acc[key] = p;
                        return acc;
                    }, {}),
                );

                setProducts(unique);
                setNoRecords(unique.length === 0);
            } catch (err) {
                console.error("Search error:", err);
                setNoRecords(true);
            }
        },
        [effectiveDealerId],
    );

    /* =========================
       TABLE
    ========================== */
    const memoizedReactTable = useMemo(() => {
        const baseColumns = [
            {
                accessor: "id",
                Cell: ({ row }) => {
                    const pathImage = row.original.pathImage;
                    const [make] = row.original.slugs || [];
                    const src = isURL(pathImage)
                        ? pathImage
                        : getProductImageUrl(
                            make?.slug,
                            row.original.part_number,
                            row.original.path_image,
                        );

                    return (
                        <Radio
                            onClick={() => {
                                const product = row.original;
                                const coreValue = parseFloat(product.core);
                                const priceValue = parseFloat(product.price?.price);

                                let finalPrice = priceValue;
                                if (!isNaN(coreValue) && !isNaN(priceValue)) {
                                    finalPrice = priceValue + coreValue;
                                }

                                onProductSelect({
                                    ...product,
                                    pathImage: src,
                                    price: {
                                        ...(product.price || {}),
                                        price: finalPrice,
                                    },
                                });
                            }}
                        />
                    );
                },
            },
            {
                accessor: "img",
                Cell: ({ row }) => {
                    const pathImage = row.original.pathImage;
                    const [make] = row.original.slugs || [];
                    const src = isURL(pathImage)
                        ? pathImage
                        : getProductImageUrl(
                            make?.slug,
                            row.original.part_number,
                            row.original.path_image,
                        );

                    return (
                        <div
                            style={{
                                width: 100,
                                height: 50,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <img
                                src={src}
                                alt="product"
                                style={{
                                    maxWidth: "100%",
                                    maxHeight: "100%",
                                    objectFit: "contain",
                                }}
                                onError={(e) => {
                                    e.currentTarget.onerror = null; // evita loop infinito
                                    e.currentTarget.src = "/not-available.png";
                                }}
                            />
                        </div>
                    );
                },
            },
            { Header: "Part number", accessor: "part_number" },
            {
                Header: "Price",
                accessor: "price.price",
                Cell: ({ value }) => <CurrencyValueDisplay value={Number(value).toFixed(2)} />,
            },
            { Header: "Name", accessor: "name" },
        ];

        const vehicleColumns =
            dealerCatalogs.length === 1
                ? []
                : [
                    { Header: "Make", accessor: "slugs[0].name" },
                    { Header: "Year", accessor: "slugs[1].name" },
                    { Header: "Model", accessor: "slugs[2].name" },
                    { Header: "Trim", accessor: "slugs[3].name" },
                    { Header: "Engine", accessor: "slugs[4].name" },
                ];

        return (
            <ReactTableV2
                columns={[...baseColumns, ...vehicleColumns]}
                data={products}
                pageCount={100}
                fetchData={({ pageIndex, pageSize }) => {
                    const isMotor = dealerCatalogs.some((dc) => dc.catalogName === "motor");
                    const start = (pageIndex - 1) * pageSize;

                    if (isMotor && pageIndex > 1) {
                        setProducts(accumulatedRecords.slice(start, start + pageSize));
                    }

                    if (!isMotor && pageIndex > 1) {
                        searchProductAllCatalogs(partNumber, dealerCatalogs, start, pageSize);
                    }
                }}
            />
        );
    }, [products, dealerCatalogs]);

    /* =========================
       RENDER
    ========================== */
    return (
        <>
            {products.length === 0 && !noRecords && <LinearProgress />}

            {products.length === 0 ? (
                <SkeletonTable
                    noRecords={noRecords}
                    noRecordsMessage={`No records found for part number ${partNumber}.`}
                    rowCount={10}
                    columnNames={[
                        "Part number",
                        "Price",
                        "Name",
                        ...(dealerCatalogs.length === 1
                            ? []
                            : ["Make", "Year", "Model", "Trim", "Engine"]),
                    ]}
                />
            ) : (
                memoizedReactTable
            )}
        </>
    );
}
